// src/com/auth_table/AuthTable.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
    TableCell, TableContainer, Table, TableHead, TableRow, TableBody, Chip, CircularProgress, IconButton
} from '@mui/material';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import DoNotTouchIcon from '@mui/icons-material/DoNotTouch';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import SearchBox from '../../common/SearchBox';
import {ContentConainer, ContentHeader, ControlBox, ToolBox} from '../../common/TopViewControl';
import { AppObject, AppStyle, AppWord, CodeGroup } from '../../value/AppConst';
import { authClassListRepo, authTableTreeRepo, codeTreeRepo, userInfoRepo } from '../../value/AppModel';
import { LIST_AUTH_CLASS } from '../auth_class/AuthClassGql';
import { useAlertSetter } from '../../api/app-hook';
import { GRANT_AUTH_TABLE, LIST_AUTH_TABLE, makeAuthTableToTree } from './AuthTableGql';
import ValueMisc from '../../value/ValueMisc';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { useSnackbar } from 'notistack';

const TitleCell = styled(TableCell)({
    fontSize: '0.8rem', color: 'white', padding: 5, backgroundColor: AppStyle.PrimaryRGB,
    textAlign: 'center', fontWeight: 'bold'
});
const ValueCell = styled(TableCell)({ padding: 5, textAlign: 'center', });

function needToShow(row, searchText) {
    if(!searchText || searchText.trim()==='') return true;
    const st = searchText.trim().toLowerCase();
    if(row.jobName.toLowerCase().indexOf(st)>=0) return true;
    if(row.label && row.label.toLowerCase().indexOf(st)>=0) return true;
    return false;
}

/**
 * 권한관리. (프로시저에 대한 실행 권한)
 */
export default function AuthTable({
    viewServiceTags // for switching view
}) {
    const sessionInfo = useReactiveVar(userInfoRepo);
    const codeTree = useReactiveVar(codeTreeRepo);
    const iAmSuperAdmin = useMemo(()=>{
        if(sessionInfo && sessionInfo.user.userId) {
            const superAdmins = ValueMisc.getCodes(codeTree, CodeGroup.SUPER_ADMIN);
            return superAdmins.find((sa) => sa.codeId===sessionInfo.user.userId)!==undefined;
        }
        return false;
    }, [codeTree, sessionInfo]);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const authClassListData = useReactiveVar(authClassListRepo); // authClassListData.records
    const [searchText, setSearchText] = useState('');
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const authDataSet = useMemo(() => {
        const dataSet = {
            headers:[{authClassId:'0', className:'잠시만 기다려 주세요...'}],
            rows:[]
        };
        if(authClassListData && authClassListData.records && authClassListData.records.length>0) {
            dataSet.headers = authClassListData.records.filter((ac) => ac.authClassId!=='0')
                .sort((a,b) => parseInt(a.authClassId)-parseInt(b.authClassId));
            if(authTableTree && authTableTree.tree) {
                const jobNames = Object.keys(authTableTree.tree).sort();
                for(const jobName of jobNames) {
                    const row = {
                        jobName,
                        label: authTableTree.tree[jobName].label,
                        allow: {}
                    };
                    for(const id of authTableTree.tree[jobName].allow) {
                        row.allow[id] = true;
                    }
                    dataSet.rows.push(row);
                }
            }
        }
        return dataSet;
    }, [authTableTree, authClassListData])
    const loadRef = useRef(0);
    const [getAuthClassList, responseAuthClassList] = useLazyQuery(LIST_AUTH_CLASS, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetAuthClassList(data, option)},
        onError: (error) => addAlert({title: '사용자 권한 목록 조회 오류', message: [error.message]})
    });
    const [getAuthTableList, responseAuthTableList] = useLazyQuery(LIST_AUTH_TABLE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetAuthTableList(data, option)},
        onError: (error) => addAlert({title: '권한테이블 목록 조회 오류', message: [error.message]})
    });
    const [grantAuthTable, responseGrantAuthTable] = useMutation(GRANT_AUTH_TABLE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGrantAuthTable(data, option)},
        onError: (error) => addAlert({title: '권한 설정 오류', message: [error.message]})
    });

    useEffect(() => {
        if(loadRef.current===0) {
            loadRef.current = 1;
            getAuthClassList();
            getAuthTableList();
        }
    }, []);

    const onCompleteGetAuthClassList = (data, clientOption) => {
        if(data.authClassList) {
            authClassListRepo({when:new Date().getTime()/1000, records:data.authClassList});
        }
    };

    // authTableTree: {jobName: {allow: ['1','2','3'], label: '작업명'}, ...}
    const onCompleteGetAuthTableList = (data, clientOption) => {
        if(data.authTableList) {
            const secNow = new Date().getTime()/1000;
            const tree = makeAuthTableToTree(data.authTableList);
            authTableTreeRepo({when:secNow, tree});
        }
    };

    const onCompleteGrantAuthTable = (data, clientOption) => {
        if(data.authTableGrant.ok) {
            getAuthTableList();
        }
        else addAlert({title:'권한 설정 오류', message:[data.authTableGrant.message]});
    };

    const onClickReload = () => {
        const diff = (new Date().getTime())/1000 - authTableTree.when;
        if(diff < 60) {
            enqueueSnackbar(AppWord.ReloadNotInOneMinute, {variant:'info', autoHideDuration:2000});
        }
        else {
            getAuthTableList();
        }
    };

    const onClickGrant = async (row, header, allowUse) => {
        // row.jobName, header.authClassId
        const job = row.label || row.jobName;
        const authClass = header.className;
        
        const confirm = await openConfirmDialog({
            title: '권한 설정',
            content: [
                `${authClass} 사용자에 대해 "${job}" 작업권한을 ${allowUse ? '허용' : '허용금지'} 하시겠습니까?`,
                '효력은 즉시 발생합니다.'
            ]
        });

        if(confirm) {
            grantAuthTable({variables:{authTable:{jobName: row.jobName, authClassId: header.authClassId, allowUse}}});
        }
    };

    const onChangeSearchText = (text) => {
        setSearchText(text);
    };

    const loadingAny = responseAuthClassList.loading || responseAuthTableList.loading || responseGrantAuthTable.loading;

    const renderValueCell = (row, header) => {
        const allowed = row.allow[header.authClassId]===true;
        if(iAmSuperAdmin) {
            return(
                <ValueCell
                    key={`${row.jobName}_${header.authClassId}`}
                >
                    <Chip
                        size={AppWord.SMALL}
                        label={allowed ? '허용' : '불허'}
                        onClick={() => onClickGrant(row, header, !allowed)}
                        color={allowed ? AppStyle.mui.info : AppStyle.mui.default}
                        variant={allowed ? AppStyle.mui.contained : AppStyle.mui.outlined}
                        icon={allowed ? <CheckCircleIcon /> : <DoNotTouchIcon />}
                        disabled={loadingAny}
                    />
                </ValueCell>
            );
        }
        return(
            <ValueCell
                key={`${row.jobName}_${header.authClassId}`}
            >
                {
                    allowed ?
                    <VerifiedUserIcon style={{color: AppStyle.InfoRGB}} /> :
                    <DoNotTouchIcon style={{color: 'lightgray'}} />
                }
            </ValueCell>
        );
    };
    /*
    헤더: 각 권한 클래스명: {'1':'관리자', '2':'통합운송', ...}
    로우: 각 작업명과 토글 컨트롤러. authTableTree.tree의 key를 sort해서 로우로 표시.
    (authTableTree.tree.someJobName : {allow: ['1','2','3'], label: '작업명'})
    로우 내 각 셀: TableCell 안에 개별 컨트롤러 넣어서 사용. 
    로우 내용은 authTableTree: {jobName: {allow: ['1','2','3'], label: '작업명'}, ...}의 특정 jobName...
    */
    return(
        <ContentConainer style={{position:'relative'}}>
            {viewServiceTags}
            <ContentHeader>
                <ControlBox>
                    <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    />
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} style={{margin: 8}}/>
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>
                    }
                    <span style={{marginLeft:10}}>
                        {
                            iAmSuperAdmin ?
                            '버튼을 클릭하여 권한을 변경할 수 있습니다.' :
                            '권한 변경은 본사 관리자에게 문의하세요.'
                        }
                        <span style={{marginLeft:10}}>
                            메뉴사용 권한과는 별개의 권한입니다.
                        </span>
                    </span>
                </ToolBox>
            </ContentHeader>
            <TableContainer style={{flexGrow:1}}>
                <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                        <TableRow style={{backgroundColor:AppStyle.PrimaryRGB}}>
                            <TitleCell>작업</TitleCell>
                            {
                                authDataSet.headers.map((header) => {
                                    return (
                                        <TitleCell key={`authclass_${header.authClassId}`}
                                            style={{maxWidth: 60}}
                                        >
                                            {header.className}
                                        </TitleCell>
                                    );
                                })
                            }
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {
                            authDataSet.rows.filter((row) => needToShow(row, searchText)).map((row) => {
                                return(
                                    <TableRow key={row.jobName} hover>
                                        <ValueCell>
                                            {row.label || row.jobName}
                                        </ValueCell>
                                        {
                                            authDataSet.headers.map((header) => renderValueCell(row, header))
                                        }
                                    </TableRow>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </ContentConainer>
    );
}
